import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { badge } from '../../lib/format';
import { card, cardPad } from '../../lib/ui';

const VAT_RATE = 0.07;
const thb = (n: number) => `$${n.toLocaleString()}`;
const numOf = (v: string) => Number((v || '').replace(/[^\d.]/g, '')) || 0;

interface Inv { no: string; date: string; amount: number; total: number; status: string; }

export default function SupplierPoDetail() {
  const { state, go, set, L } = useApp();
  const po = state.openedPo;
  const tStatus = (s: string) => L(s, ({ 'Active': 'ใช้งานอยู่', 'Pending Approval': 'รออนุมัติ', 'Received': 'ได้รับแล้ว' } as Record<string, string>)[s] ?? s);

  const poValue = numOf(po?.value ?? '0');
  const seq = (po?.id.match(/\d+$/)?.[0] ?? '0001');
  const prefix = (po?.sup ?? 'SUP').split(' ').map((w) => w[0]).slice(0, 3).join('').toUpperCase();

  const [invoices, setInvoices] = useState<Inv[]>(
    po?.invoiced ? [{ no: `${prefix}-INV-2026-${seq}`, date: '2026-07-02', amount: poValue, total: Math.round(poValue * (1 + VAT_RATE)), status: 'Received' }] : [],
  );
  const [uploadOpen, setUploadOpen] = useState(false);
  const [invNo, setInvNo] = useState('');
  const [invDate, setInvDate] = useState('');
  const [amount, setAmount] = useState('');
  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState<{ invNo?: string; amount?: string; file?: string }>({});

  if (!po) return <div style={cardPad(24)}>{L('No purchase order selected.', 'ยังไม่ได้เลือกใบสั่งซื้อ')} <button onClick={() => go('myorders')} style={{ color: 'var(--color-link)', fontWeight: 600 }}>{L('Back to My Purchase Orders', 'กลับไปหน้าใบสั่งซื้อของฉัน')}</button></div>;

  const amt = numOf(amount);
  const vat = Math.round(amt * VAT_RATE);
  const st = badge(po.status ?? 'Active');

  function openUpload() {
    setInvNo(`${prefix}-INV-2026-${seq}`);
    setInvDate('2026-07-02');
    setAmount(String(poValue));
    setFileName(`Invoice-${po!.id}.pdf`);
    setErrors({});
    setUploadOpen(true);
  }

  function submit() {
    const next: typeof errors = {};
    if (!invNo.trim()) next.invNo = L('Invoice number is required.', 'ต้องระบุเลขที่ใบแจ้งหนี้');
    if (!(amt > 0)) next.amount = L('Enter a valid amount greater than 0.', 'กรอกจำนวนเงินที่ถูกต้องมากกว่า 0');
    if (!fileName) next.file = L('Attach the invoice file.', 'แนบไฟล์ใบแจ้งหนี้');
    if (Object.keys(next).length) { setErrors(next); return; }
    setInvoices((list) => [...list, { no: invNo.trim(), date: invDate || '2026-07-02', amount: amt, total: amt + vat, status: 'Received' }]);
    setUploadOpen(false);
  }

  const details: [string, string, boolean][] = [
    [L('PO Number', 'เลขที่ใบสั่งซื้อ'), po.id, true],
    [L('Supplier', 'ผู้จัดจำหน่าย'), po.sup, false],
    [L('PO Value', 'มูลค่าใบสั่งซื้อ'), thb(poValue), true],
    [L('Status', 'สถานะ'), tStatus(po.status ?? 'Active'), false],
  ];

  return (
    <div>
      <button onClick={() => go('myorders')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-fg-2)', marginBottom: 16 }}><Icon name="arrow-left" />{L('Back to My Purchase Orders', 'กลับไปหน้าใบสั่งซื้อของฉัน')}</button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{po.id}</div>
          <div style={{ fontSize: 13, color: 'var(--color-fg-3)' }}>{po.desc}</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, background: st.bg, color: st.fg }}>{tStatus(po.status ?? 'Active')}</span>
      </div>

      {/* PO details */}
      <div style={{ ...cardPad(20), marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>{L('Purchase Order Details', 'รายละเอียดใบสั่งซื้อ')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
          {details.map(([k, v, mono]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
              <span style={{ color: 'var(--color-fg-3)' }}>{k}</span>
              <span style={{ fontWeight: 600, fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Invoices */}
      <div style={cardPad(0, 'auto')}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ fontWeight: 700 }}>{L('Invoices', 'ใบแจ้งหนี้')} ({invoices.length})</div>
          <button onClick={openUpload} className="hv-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '7px 13px', borderRadius: 'var(--radius-sm)', background: 'var(--color-primary)', color: '#fff' }}><Icon name="upload" />{L('Upload Invoice', 'อัปโหลดใบแจ้งหนี้')}</button>
        </div>
        {invoices.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <Icon name="receipt" style={{ fontSize: 26, color: 'var(--color-fg-3)' }} />
            <div style={{ fontWeight: 700, marginTop: 10 }}>{L('No Invoice Submitted', 'ยังไม่ได้ส่งใบแจ้งหนี้')}</div>
            <div style={{ fontSize: 13, color: 'var(--color-fg-3)', marginTop: 4 }}>{L('Upload your invoice for this purchase order. Payment begins once it clears the 3-way match.', 'อัปโหลดใบแจ้งหนี้สำหรับใบสั่งซื้อนี้ การชำระเงินจะเริ่มเมื่อผ่านการจับคู่ 3 ทาง')}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
            <thead>
              <tr style={{ background: 'var(--color-mist)', borderBottom: '1px solid var(--color-border)' }}>
                {[L('Invoice No', 'เลขที่ใบแจ้งหนี้'), L('Date', 'วันที่'), L('Amount (excl. VAT)', 'จำนวนเงินก่อน VAT'), L('Total incl. VAT', 'รวม VAT'), L('Status', 'สถานะ'), ''].map((h, i) => (
                  <th key={i} style={{ textAlign: i === 2 || i === 3 ? 'right' : 'left', padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map((iv, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '11px 16px', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{iv.no}</td>
                  <td style={{ padding: '11px 16px', color: 'var(--color-fg-2)' }}>{iv.date}</td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{thb(iv.amount)}</td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{thb(iv.total)}</td>
                  <td style={{ padding: '11px 16px' }}><span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: 'var(--color-success-bg)', color: 'var(--color-success-ink)' }}>{tStatus(iv.status)}</span></td>
                  <td style={{ padding: '11px 16px', textAlign: 'right' }}><button onClick={() => set({ invoiceDocOpen: true })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--color-blue)' }}><Icon name="eye" />{L('View', 'ดู')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {uploadOpen && createPortal((
        <div onClick={() => setUploadOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(14,23,38,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, animation: 'aibid-scrim .2s', padding: 20, overflow: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} className="aibid-fade" style={{ ...card, width: 560, maxWidth: '96vw', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ fontWeight: 700 }}>{L('Upload Invoice', 'อัปโหลดใบแจ้งหนี้')} — {po.id}</div>
              <button onClick={() => setUploadOpen(false)} style={{ color: 'var(--color-fg-3)', fontSize: 18 }}><Icon name="x" /></button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{L('Invoice Number', 'เลขที่ใบแจ้งหนี้')} *</label>
                  <input value={invNo} onChange={(e) => { setInvNo(e.target.value); setErrors((x) => ({ ...x, invNo: undefined })); }} placeholder={L('e.g. BKI-INV-2026-0091', 'เช่น BKI-INV-2026-0091')} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errors.invNo ? 'var(--color-error)' : 'var(--color-border-strong)'}`, borderRadius: 'var(--radius-sm)', background: 'var(--color-white)' }} />
                  {errors.invNo && <span style={{ fontSize: 11, color: 'var(--color-error)' }}>{errors.invNo}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{L('Invoice Date', 'วันที่ใบแจ้งหนี้')}</label>
                  <input type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-white)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{L('Amount excl. VAT (USD)', 'จำนวนเงินก่อน VAT (ดอลลาร์)')} *</label>
                <input inputMode="numeric" value={amount} onChange={(e) => { setAmount(e.target.value); setErrors((x) => ({ ...x, amount: undefined })); }} placeholder={L('e.g. 4620000', 'เช่น 4620000')} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errors.amount ? 'var(--color-error)' : 'var(--color-border-strong)'}`, borderRadius: 'var(--radius-sm)', background: 'var(--color-white)', fontFamily: 'var(--font-mono)' }} />
                {amt > 0 && <span style={{ fontSize: 11, color: 'var(--color-fg-3)', marginTop: 4, display: 'block' }}>{L('VAT 7%', 'VAT 7%')}: {thb(vat)} · {L('Total incl. VAT', 'รวม VAT')}: <b style={{ fontFamily: 'var(--font-mono)' }}>{thb(amt + vat)}</b></span>}
                {errors.amount && <span style={{ fontSize: 11, color: 'var(--color-error)' }}>{errors.amount}</span>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{L('Invoice File', 'ไฟล์ใบแจ้งหนี้')} *</label>
                <button type="button" onClick={() => { setFileName(`Invoice-${po.id}.pdf`); setErrors((x) => ({ ...x, file: undefined })); }} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 18, cursor: 'pointer', border: `1.5px dashed ${fileName ? 'var(--color-success)' : errors.file ? 'var(--color-error)' : 'var(--color-border-strong)'}`, borderRadius: 'var(--radius-sm)', background: fileName ? 'var(--color-success-bg)' : 'var(--color-white)' }}>
                  <Icon name={fileName ? 'paperclip' : 'upload'} style={{ fontSize: 18, color: fileName ? 'var(--color-success)' : 'var(--color-fg-3)' }} />
                  <span style={{ fontSize: 13, fontWeight: fileName ? 600 : 400, color: fileName ? 'var(--color-success-ink)' : 'var(--color-fg-3)' }}>{fileName || L('Click to attach the invoice PDF', 'คลิกเพื่อแนบไฟล์ใบแจ้งหนี้ PDF')}</span>
                </button>
                {errors.file && <span style={{ fontSize: 11, color: 'var(--color-error)' }}>{errors.file}</span>}
              </div>
              <p style={{ fontSize: 11, color: 'var(--color-fg-3)' }}>{L('The system extracts invoice data via OCR and runs the 3-way match against the PO and goods receipt automatically.', 'ระบบจะดึงข้อมูลใบแจ้งหนี้ด้วย OCR และทำการจับคู่ 3 ทางกับใบสั่งซื้อและใบรับสินค้าโดยอัตโนมัติ')}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', background: 'var(--color-mist)', borderTop: '1px solid var(--color-border)' }}>
              <button onClick={() => setUploadOpen(false)} style={{ padding: '8px 14px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13, background: 'var(--color-white)' }}>{L('Cancel', 'ยกเลิก')}</button>
              <button onClick={submit} className="hv-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--color-primary)', color: '#fff', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13 }}><Icon name="upload" />{L('Submit Invoice', 'ส่งใบแจ้งหนี้')}</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
